import React, { useContext } from "react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { getAppConfig } from "@config/appConfig";
import { PLACEHOLDER_MOVIE_POSTER } from "@config/ImageSizes";
import toSeoUrl from "@functions/toSeoUrl";
import { SearchContext } from "@context/SearchContext";
import PlayIcon from "@components/Icons/PlayIcon";
import TrailerButton from "@components/TrailerButton";
import V3SessionButton from "@components/V3/V3SessionButton";
import VenueNearbyCinemas from "@components/V3/VenueDetails/VenueNearbyCinemas";
import { getImageAtSize } from "@utils/imageUtils";
import { getSessionType } from "@utils/ticketUtils";
import "./style.scss";
// Keep in sync with CinemaGroupsTable::HIDE_SESSIONS_EXEMPT_SUBSCRIBER_GROUPS
// RACV (61) and Cliqit (62) are exempt from the hide-sessions feature

const VenueSessionList = ({ selectedDate, sessions, venue }) => {
    const config = getAppConfig();
    const { venueName } = useContext(SearchContext);

    const fallbackImage = `${config?.cdnUrl}${PLACEHOLDER_MOVIE_POSTER}`;

    const onImageError = (e) => {
        e.target.src = fallbackImage;
    };

    const sessionsArray =
        sessions && typeof sessions === "object" && !Array.isArray(sessions)
            ? Object.values(sessions)
            : Array.isArray(sessions)
                ? sessions
                : [];

    const sortedSessionsArray = [...sessionsArray].sort((a, b) => {
        if (!Array.isArray(a) || a.length === 0) return 1;
        if (!Array.isArray(b) || b.length === 0) return -1;

        // Primary sort by number of sessions
        const sessionCountDiff = b.length - a.length;
        if (sessionCountDiff !== 0) return sessionCountDiff;

        // Secondary sort is alphabetically
        const titleA = (a[0]?.movie_title ?? "").toLowerCase();
        const titleB = (b[0]?.movie_title ?? "").toLowerCase();
        return titleA.localeCompare(titleB);
    });

    const hasSessions =
        Array.isArray(sessionsArray) &&
        sessionsArray.some((sessionList) => Array.isArray(sessionList) && sessionList.length > 0);

    if (!hasSessions) {
        const showNearby =
            Number(venue?.hide_sessions_show_nearby) === 1;
        return (
            <>

                {showNearby ? (
                    <div className="show-nearby__wrapper">
                        <p className="show-nearby__title">
                            Good news - we&apos;ve found great alternatives nearby
                        </p>
                        <p className="show-nearby__text">
                            The venue you selected is not a participating cinema. We&apos;ve found some great nearby cinemas that offer a fantastic movie experience and excellent value.
                        </p>
                        <VenueNearbyCinemas venue={venue} />
                    </div>
                ) : (
                    <div className="sessions-available__message">
                        <p className="sessions-available__message-title">
                            Sorry, we couldn&apos;t find any results for your chosen cinema
                        </p>
                        <p className="sessions-available__message-text">
                            Please select a different cinema to find sessions.
                        </p>
                    </div>

                )}
            </>
        );
    }

    return (
        <div className="sessions-list__movies">
            <div className="sessions-list__movies-list">
                {sortedSessionsArray.map((sessionList, idx) => {
                    if (!Array.isArray(sessionList) || sessionList.length === 0) return null;
                    const first = sessionList[0];
                    const safeMovieTitle = first.movie_title ?? "";
                    const movieUrl = `/movie/${first.movie_id}/${toSeoUrl(safeMovieTitle)}${venueName ? `?venue_name=${toSeoUrl(venueName)}` : ""}`;


                    return (
                        <div className="sessions-list__movies-item-wrapper" key={first.movie_id || idx}>
                            <div className="sessions-list__movies-item">
                                <Link
                                    className="sessions-list__movies-link"
                                    to={movieUrl}
                                >
                                    <img
                                        className="sessions-list__movies-image"
                                        src={getImageAtSize(first.movie_image, 280) || fallbackImage}
                                        alt={safeMovieTitle}
                                        onError={onImageError}
                                    />
                                </Link>

                                <div className="sessions-list__movies-content">
                                    <Link
                                        className="sessions-list__movies-title"
                                        to={movieUrl}
                                    >
                                        {safeMovieTitle}
                                    </Link>

                                    <ul className="sessions-list__movies-meta">
                                        {first.movie_rating && (
                                            <li className="sessions-list__movies-meta-item">{first.movie_rating}</li>
                                        )}
                                        {first.movie_run_time && (
                                            <li className="sessions-list__movies-meta-item">{first.movie_run_time}mins</li>
                                        )}
                                        {first.movie_genre && (
                                            <li className="sessions-list__movies-meta-item">{first.movie_genre}</li>
                                        )}
                                    </ul>

                                    <p className="sessions-list__movies-description">{first.movie_blurb}</p>
                                    {first.trailer_url ? (
                                        <TrailerButton
                                            extraClass="sessions-list__movies-trailer"
                                            type="mp4"
                                            trailerLink={first.trailer_url}
                                        >
                                            <PlayIcon className="sessions-list__movies-icon-svg" />
                                            <span className="sessions-list__movies-trailer-text">
                                                Official Trailer
                                            </span>
                                        </TrailerButton>
                                    ) : null}
                                </div>
                            </div>

                            <h3 className="sessions-list__sessions-available-date">
                                {selectedDate
                                    ? format(new Date(selectedDate), "EEE d MMM")
                                    : null}
                            </h3>
                            
                            <div className="sessions-list__sessions-available-list">
                                {sessionList.map((session) => {
                                    const experience_type = getSessionType(session);
                                    return (
                                        <V3SessionButton
                                            key={session.id}
                                            session={{ ...session, experience_type }}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default VenueSessionList;
